import { LINK_GAP, ROW_H, type DependencyType } from './constants'

export interface Endpoint { x1: number; x2: number; y: number }
export type Point = [number, number]
/** `hitPoints` mirrors `points` for the invisible hit-stroke (Task 18 fix round 1) — trimmed on
 * whichever end(s) sit at a bar's own right edge (x2), so it never covers that bar's resize/link
 * handles. `points` (and the arrowhead) are untouched — only the hit-test geometry changes. */
export interface LinkPath { points: Point[]; hitPoints: Point[]; arrow: { x: number; y: number; dir: 'right' | 'left' } }

/**
 * MS Project style orthogonal link. The line leaves the predecessor's finish (FS, FF) to the right or its
 * start (SS, SF) to the left by `gap`, runs vertically to the successor row and enters the successor's
 * start (FS, SS) moving right or its finish (FF, SF) moving left. When that last horizontal move would go
 * the wrong way, the line detours half a row below (or above, when the successor is above) the predecessor.
 */
export const routeLink = (type: DependencyType, from: Endpoint, to: Endpoint, gap = LINK_GAP, rowH = ROW_H): LinkPath => {
  const leavesFinish = type === 'FS' || type === 'FF'
  const entersStart = type === 'FS' || type === 'SS'
  const fromX = leavesFinish ? from.x2 : from.x1
  const outDir = leavesFinish ? 1 : -1
  const toX = entersStart ? to.x1 : to.x2
  const inDir = entersStart ? 1 : -1
  const exitX = fromX + outDir * gap
  const entryX = toX - inDir * gap
  const points: Point[] = [[fromX, from.y], [exitX, from.y]]
  if ((toX - exitX) * inDir > 0 && to.y !== from.y) {
    points.push([exitX, to.y], [toX, to.y])
  } else {
    const midY = from.y + (to.y >= from.y ? 1 : -1) * (rowH / 2)
    points.push([exitX, midY], [entryX, midY], [entryX, to.y], [toX, to.y])
  }
  // `leavesFinish` / `!entersStart` are exactly the cases where this endpoint IS a bar's x2 (FS/FF
  // leave from x2; FF/SF enter at x2) — the only place `TaskBar`/`MilestoneMarker` ever draw a
  // handle. `exitX`/`entryX` already sit `gap` px from that edge, so starting/ending the hit path
  // there instead of at `fromX`/`toX` clears the handle footprint exactly, as long as
  // `gap >= LINK_HANDLE_GAP + LINK_HANDLE_R` (true for the default `LINK_GAP`).
  const hitPoints: Point[] = points.map((p) => [p[0], p[1]] as Point)
  if (leavesFinish) hitPoints[0] = [exitX, hitPoints[0][1]]
  if (!entersStart) { const last = hitPoints.length - 1; hitPoints[last] = [entryX, hitPoints[last][1]] }
  return { points, hitPoints, arrow: { x: toX, y: to.y, dir: inDir === 1 ? 'right' : 'left' } }
}

export const toSvgPath = (points: Point[]): string => points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join(' ')
